import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  Field,
  FieldError,
  FieldLabel,
} from '@monksflow/monks-ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonaCreatableSelectFieldProps } from './PersonaFields.types';

/**
 * Single-value field that also accepts a value the workspace has not used
 * before — the segment picker, where naming a new segment IS the common case.
 */
export const PersonaCreatableSelectField = ({
  label,
  value,
  onChange,
  placeholder,
  required,
  error,
  disabled,
  options,
}: PersonaCreatableSelectFieldProps) => {
  const { t } = useTranslation('audiences');
  const id = useId();
  const [query, setQuery] = useState(value);

  const trimmedQuery = query.trim();
  const canAdd = trimmedQuery.length > 0 && !options.includes(trimmedQuery);
  const items = canAdd ? [...options, trimmedQuery] : [...options];

  return (
    <Field data-invalid={Boolean(error) || undefined}>
      <FieldLabel htmlFor={id}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </FieldLabel>
      <Combobox
        items={items}
        value={value}
        onValueChange={(next: string | null) => {
          const resolved = next ?? '';
          onChange(resolved);
          setQuery(resolved);
        }}
        inputValue={query}
        onInputValueChange={(next: string) => {
          setQuery(next);
          // Naming a new segment IS the common case here, and picking the
          // "Add …" row is not the only way a user expects to do it. Without
          // this, typed text never reaches the form: the field looks filled in
          // while the required value stays empty and Continue stays disabled
          // with nothing on screen explaining why. `inputValue` is controlled
          // here, so an empty string only ever arrives from the user clearing
          // the field.
          onChange(next);
        }}
        disabled={disabled}
      >
        <ComboboxInput
          id={id}
          placeholder={placeholder}
          aria-invalid={Boolean(error) || undefined}
        />
        {/* Radix's modal Sheet sets `pointer-events: none` on the body, and this
            popup portals outside the dialog, so it inherits it and every option
            renders but refuses the mouse. Re-enabling pointer events on the popup
            itself is what makes the list usable inside a sheet. */}
        <ComboboxContent className="pointer-events-auto">
          <ComboboxList>
            {items.map((item) => (
              <ComboboxItem key={item} value={item}>
                {item === trimmedQuery && canAdd
                  ? t('audiences.personaTagField.addValue', { value: item })
                  : item}
              </ComboboxItem>
            ))}
          </ComboboxList>
          <ComboboxEmpty>{t('audiences.personaTagField.noOptions')}</ComboboxEmpty>
        </ComboboxContent>
      </Combobox>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
};

PersonaCreatableSelectField.displayName = 'PersonaCreatableSelectField';
